<template>
  <q-card class="q-pa-sm" style="width: 100%">
    <q-card-section class="bg-primary text-white q-py-sm row justify-between items-center">
      <div class="text-weight-bold cursor-pointer" @click="handleEditClick">
        <q-icon name="add" class="q-mr-sm" />
        {{ isEditing ? 'Edit' : 'Add/Manage' }} {{ role === 'home' ? 'Home' : 'Visiting' }} Lineup
      </div>

      <!-- Toggle between Single Edit and Quick Grid -->
      <q-btn-toggle
        v-if="modelValue && !isEditing"
        v-model="editMode"
        no-caps
        unelevated
        dense
        toggle-color="white"
        toggle-text-color="primary"
        color="primary"
        text-color="white"
        style="border: 1px solid white"
        :options="[
          { label: 'Single', value: 'single' },
          { label: 'Grid Edit', value: 'grid' },
        ]"
      />
    </q-card-section>

    <!-- ========================================== -->
    <!-- MODE 1: SINGLE BATTER VERBOSE EDIT FORM    -->
    <!-- ========================================== -->
    <q-card-section class="q-gutter-md q-mt-sm" v-show="modelValue && editMode === 'single'">
      <div class="row q-gutter-x-sm">
        <q-input
          v-model.number="uniformNumberModel"
          label="Number"
          type="number"
          outlined
          dense
          class="col-3"
          autofocus
        />
        <q-input v-model="player.name" label="Player Name" outlined dense class="col" />
      </div>

      <div class="row q-gutter-x-sm">
        <q-select
          v-model="player.bats"
          :options="['R', 'L', 'S']"
          label="Bats"
          outlined
          dense
          class="col"
        />
        <q-select
          v-model="player.throws"
          :options="['R', 'L']"
          label="Throws"
          outlined
          dense
          class="col"
        />
      </div>

      <div class="row q-gutter-x-sm">
        <q-input
          v-model.number="battingOrder"
          label="Batting Order"
          type="number"
          outlined
          dense
          class="col"
          :disable="isEditing"
        />
        <q-select
          v-model="startingPosition"
          :options="positionOptions"
          emit-value
          map-options
          label="Fielding Position"
          outlined
          dense
          class="col-7"
        />
      </div>
    </q-card-section>

    <!-- ========================================== -->
    <!-- MODE 2: QUICK GRID & AUTO-GENERATE         -->
    <!-- ========================================== -->
    <q-card-section class="q-mt-sm q-pa-xs" v-show="modelValue && editMode === 'grid'">
      <!-- Auto-Generate Button (Only shows if lineup is empty) -->
      <div v-if="localLineup.length === 0" class="text-center q-py-lg">
        <q-icon name="bolt" size="3rem" color="warning" class="q-mb-sm" />
        <div class="text-subtitle1 text-grey-8 q-mb-md">No batters found in lineup.</div>
        <q-btn
          color="primary"
          unelevated
          icon="group_add"
          :label="lineupAutoGenLabel"
          @click="autoGenerateLineup"
        />
      </div>

      <!-- Quick Edit Grid (Shows if lineup has players) -->
      <div v-else>
        <div
          class="row q-col-gutter-xs text-weight-bold text-caption text-grey-8 q-mb-sm text-center"
        >
          <div class="col-2">Order</div>
          <div class="col-3">Num #</div>
          <div class="col-4">Name</div>
          <div class="col-3">Pos</div>
        </div>

        <div
          v-for="(batter, idx) in localLineup"
          :key="'grid-' + idx"
          class="row q-col-gutter-xs q-mb-xs items-center"
        >
          <div class="col-2 text-center text-weight-bold text-primary">
            {{ batter.battingOrder }}
          </div>
          <div class="col-3">
            <q-input
              v-model.number="batter.player.uniformNumber"
              type="number"
              dense
              outlined
              placeholder="#"
            />
          </div>
          <div class="col-4">
            <q-input v-model="batter.player.name" dense outlined placeholder="Name" />
          </div>
          <div class="col-3">
            <q-select
              v-model="batter.startingPosition"
              :options="shortPositionOptions"
              emit-value
              map-options
              dense
              outlined
            />
          </div>
        </div>
      </div>
    </q-card-section>

    <!-- ========================================== -->
    <!-- ACTIONS                                    -->
    <!-- ========================================== -->
    <q-card-actions align="right" class="text-primary" v-show="modelValue">
      <q-btn flat label="Cancel" color="grey-7" @click="handleCancel" />

      <!-- Single Mode Save Button -->
      <!-- We no longer disable if empty name since a single space passes truthy checks natively,
           allowing fast saves purely focused on the uniform number. -->
      <q-btn
        v-if="editMode === 'single'"
        unelevated
        color="primary"
        :label="isEditing ? 'Update Batter' : 'Add to Lineup'"
        @click="saveSingleEntry"
        :disable="!player.name"
      />

      <!-- Grid Mode Save Button -->
      <q-btn
        v-if="editMode === 'grid' && localLineup.length > 0"
        unelevated
        color="positive"
        icon="check"
        label="Save Grid"
        @click="saveGridEntries"
      />
    </q-card-actions>
  </q-card>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { db } from 'src/models/db'
import type { BaseballPlayer, LineupEntry } from 'src/stores/base-types'

const props = defineProps<{
  modelValue: boolean
  role: 'home' | 'visiting'
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'saved'): void
  (e: 'update:modelValue', value: boolean): void
}>()

const gameStore = useGameStore()
const settingsStore = useSettingsStore()

// --- HELPER: GENERATE PLAYER CODE ---
const generatePlayerCode = (p: BaseballPlayer): string => {
  const activeTeam = props.role === 'home' ? gameStore.teamHome : gameStore.teamVisiting
  const teamName = activeTeam?.name || 'Unknown'
  const safeTeamName = teamName.replace(/\s+/g, '_')

  // Grab the first character. If it's empty or a space, replace it with an underscore.
  const firstChar =
    p.name && p.name.trim().length > 0 ? p.name.trim().charAt(0).replace(/\s/g, '_') : '_'

  const num = p.uniformNumber || 0
  return `${safeTeamName}_${num}_${firstChar}`
}

// --- HELPER: SYNC TO DEXIE MASTER ROSTER ---
const syncToTeamRoster = async (playersToSync: BaseballPlayer[]) => {
  const activeTeam = props.role === 'home' ? gameStore.teamHome : gameStore.teamVisiting
  if (activeTeam && activeTeam.id) {
    let hasUpdates = false

    playersToSync.forEach((p) => {
      const rosterPlayer = activeTeam.players.find((rp) => String(rp.id) === String(p.id))
      if (rosterPlayer) {
        rosterPlayer.uniformNumber = p.uniformNumber ?? 0
        rosterPlayer.name = p.name ?? ' '
        rosterPlayer.bats = p.bats ?? 'R'
        rosterPlayer.throws = p.throws ?? 'R'
        rosterPlayer.playerCode = p.playerCode ?? 'Unknown_0__'
        hasUpdates = true
      }
    })

    if (hasUpdates) {
      try {
        await db.teams.put(JSON.parse(JSON.stringify(activeTeam)))
      } catch (err) {
        console.error('Failed to sync updated batter(s) to master team roster:', err)
      }
    }
  }
}

// --- UI STATE ---
const editMode = ref<'single' | 'grid'>('single')
const isEditing = computed(() => gameStore.editingLineupEntry !== null)

const handleEditClick = () => {
  emit('update:modelValue', !props.modelValue)
}

// --- SINGLE MODE STATE ---
const player = ref<BaseballPlayer>({
  id: Date.now(),
  name: ' ', // Default to a space per user request
  uniformNumber: 0,
  bats: 'R',
  throws: 'R',
  primaryPosition: 13,
  primaryBattingOrder: 0,
  playerCode: 'Unknown_0__',
})

const uniformNumberModel = computed({
  get: () => (player.value.uniformNumber === 0 ? '' : player.value.uniformNumber),
  set: (value) => {
    player.value.uniformNumber = value === '' || value == null ? 0 : Number(value)
  },
})
const lineupAutoGenLabel = computed(
  () => 'Auto-Generate 1-' + settingsStore.defaultRosterSize + ' Lineup',
)

const battingOrder = ref<number>(1)
const startingPosition = ref<number>(10)

// --- GRID MODE STATE ---
const localLineup = ref<LineupEntry[]>([])

// Sync local grid data whenever the mode changes to 'grid'
watch(editMode, (newMode) => {
  if (newMode === 'grid') {
    const currentLineup = props.role === 'home' ? gameStore.homeLineup : gameStore.visitingLineup
    // Deep clone the lineup so grid edits don't instantly mutate Pinia state until saved
    localLineup.value = JSON.parse(JSON.stringify(currentLineup || []))
  }
})

// Standard baseball positional numbering
const positionOptions = [
  { label: '1 - Pitcher (P)', value: 1 },
  { label: '2 - Catcher (C)', value: 2 },
  { label: '3 - 1st Base (1B)', value: 3 },
  { label: '4 - 2nd Base (2B)', value: 4 },
  { label: '5 - 3rd Base (3B)', value: 5 },
  { label: '6 - Shortstop (SS)', value: 6 },
  { label: '7 - Left Field (LF)', value: 7 },
  { label: '8 - Center Field (CF)', value: 8 },
  { label: '9 - Right Field (RF)', value: 9 },
  { label: '10 - Des. Hitter (DH)', value: 10 },
  { label: '11 - Pinch Hitter (PH)', value: 11 },
  { label: '12 - Pinch Runner (PR)', value: 12 },
  { label: '13 - Extra Batter (EH)', value: 13 },
]

// Condensed options for the tight grid view
const shortPositionOptions = [
  { label: '1 (P)', value: 1 },
  { label: '2 (C)', value: 2 },
  { label: '3 (1B)', value: 3 },
  { label: '4 (2B)', value: 4 },
  { label: '5 (3B)', value: 5 },
  { label: '6 (SS)', value: 6 },
  { label: '7 (LF)', value: 7 },
  { label: '8 (CF)', value: 8 },
  { label: '9 (RF)', value: 9 },
  { label: '10 (DH)', value: 10 },
  { label: '11 (PH)', value: 11 },
  { label: '12 (PR)', value: 12 },
  { label: '13 (EH)', value: 13 },
]

// --- THE WATCHER (Single Mode Sync) ---
watch(
  () => gameStore.editingLineupEntry,
  (newVal) => {
    if (newVal) {
      player.value = { ...newVal.player }
      battingOrder.value = newVal.battingOrder
      startingPosition.value = newVal.startingPosition
      editMode.value = 'single'
    } else {
      const currentLineup =
        (props.role === 'home' ? gameStore.homeLineup : gameStore.visitingLineup) || []
      const nextOrder = currentLineup.length + 1

      battingOrder.value = nextOrder
      startingPosition.value = 13

      player.value = {
        id: Date.now(),
        name: ' ', // Default to a space
        uniformNumber: 0,
        bats: 'R',
        throws: 'R',
        playerCode: 'Unknown_0__',
      }

      if (currentLineup.length === 0) {
        editMode.value = 'grid'
      } else {
        editMode.value = 'single'
      }
    }
  },
  { immediate: true },
)

// --- ACTIONS ---

async function saveSingleEntry() {
  try {
    // 1. Compile Code based on current user inputs
    player.value.playerCode = generatePlayerCode(player.value)

    const entryToSave: LineupEntry = {
      player: { ...player.value },
      battingOrder: battingOrder.value,
      startingPosition: startingPosition.value,
      substitutions: gameStore.editingLineupEntry?.substitutions || [],
    }

    // 2. Save to active game lineup via Store
    await gameStore.saveBatter(props.role, entryToSave)

    // 3. Sync player edits back to master team table in Dexie
    await syncToTeamRoster([player.value])

    emit('saved')

    if (!isEditing.value) {
      const nextOrder = battingOrder.value + 1
      battingOrder.value = nextOrder
      startingPosition.value = 10
      player.value = {
        id: Date.now(),
        name: ' ', // Default to a space
        uniformNumber: 0,
        bats: 'R',
        throws: 'R',
        playerCode: 'Unknown_0__',
      }
    }
  } catch (error) {
    console.error('Failed to save entry:', error)
  }
}

function autoGenerateLineup() {
  const generated: LineupEntry[] = []

  for (let i = 1; i <= settingsStore.defaultRosterSize; i++) {
    const primaryPos = i <= 10 ? i : 13

    const generatedPlayer: BaseballPlayer = {
      id: Date.now() + i,
      name: ' ', // Default to a space
      uniformNumber: i,
      bats: 'R',
      throws: 'R',
      primaryPosition: primaryPos,
      primaryBattingOrder: i,
      playerCode: '', // Built below
    }

    generatedPlayer.playerCode = generatePlayerCode(generatedPlayer)

    generated.push({
      player: generatedPlayer,
      battingOrder: i,
      startingPosition: primaryPos,
      substitutions: [],
    })
  }

  localLineup.value = generated
}

async function saveGridEntries() {
  try {
    // 1. Recalculate Player Codes for entire grid
    localLineup.value.forEach((entry) => {
      entry.player.playerCode = generatePlayerCode(entry.player)
    })

    // 2. Overwrite the Pinia array directly
    if (props.role === 'home') {
      gameStore.homeLineup = localLineup.value
    } else {
      gameStore.visitingLineup = localLineup.value
    }

    // 3. Sync player edits back to master team table in Dexie
    await syncToTeamRoster(localLineup.value.map((e) => e.player))

    // 4. Save to IndexedDB via the store action
    await gameStore.saveActiveGame()

    emit('saved')
    emit('update:modelValue', false)
  } catch (error) {
    console.error('Failed to save grid:', error)
  }
}

function handleCancel() {
  gameStore.clearEditingBatter()
  emit('cancel')
}
</script>
